<template>
  <div class="create-event-page container-fluid p-0">

    <FullScreenLoader v-if="themeLoading" />

    <!-- Steps Navigation -->
    <nav v-show="!themeLoading" class="steps-navigation">
      <div class="progress-track">
        <div
          class="progress-fill"
          :style="{ width: ((currentStep - 1) / (steps.length - 1) * 100) + '%' }"
        ></div>
      </div>

      <div class="steps-row">
        <div
          v-for="step in steps"
          :key="step.id"
          :class="['step-dot-item', { active: currentStep === step.id, completed: currentStep > step.id }]"
        >
          <div class="step-dot">
            <i v-if="currentStep > step.id" class="fas fa-check"></i>
            <span v-else>{{ step.id }}</span>
          </div>
          <span class="step-dot-label">{{ step.name }}</span>
        </div>
      </div>
    </nav>

    <!-- Conteúdo -->
    <main v-show="!themeLoading" class="step-content-wrapper">
      <div class="step-inner">

      <!-- ========================
           STEP 1 — IDENTIFICAÇÃO
           ======================== -->
      <div v-show="currentStep === 1">

        <!-- Lista de customers salvos -->
        <div v-if="customerAlreadySaved && !showNewForm">

          <div class="d-flex align-items-start justify-content-between mb-3">
            <div>
              <h2 class="step-title">Quem vai presentear?</h2>
              <p class="step-subtitle mb-0">Selecione ou gerencie os perfis salvos</p>
            </div>
            <button
              :class="['btn-edit-mode', { 'is-active': editMode }]"
              @click="toggleEditMode"
              :title="editMode ? 'Cancelar' : 'Gerenciar'"
            >
              <i :class="editMode ? 'fas fa-times' : 'fas fa-pen-to-square'"></i>
            </button>
          </div>

          <!-- Lista -->
          <div class="customers-list mb-1">
            <div
              v-for="c in customerStore.customers"
              :key="c.cpf_cnpj"
              :class="['identity-card', { 'is-edit-mode': editMode, 'is-selected': selectedCpfs.includes(c.cpf_cnpj) }]"
              @click="editMode ? toggleSelectCpf(c.cpf_cnpj) : selecionarCustomer(c)"
            >
              <!-- Checkbox edit mode -->
              <div v-if="editMode" class="identity-checkbox">
                <i :class="selectedCpfs.includes(c.cpf_cnpj) ? 'fas fa-square-check' : 'far fa-square'"></i>
              </div>

              <div class="identity-avatar">
                <i class="fas fa-user"></i>
              </div>
              <div class="identity-info">
                <span class="identity-name">{{ c.name }}</span>
                <span class="identity-email">{{ c.email }}</span>
              </div>

              <span
                v-if="method === 'card' && !customerCompleto(c)"
                class="customer-incomplete-badge"
                title="Faltam dados de endereço para pagar com cartão"
              >
                <i class="fas fa-map-marker-alt me-1"></i> Completar
              </span>

              <template v-if="!editMode">
                <span v-if="saving" class="spinner-border spinner-border-sm text-pink flex-shrink-0"></span>
                <button v-else class="identity-confirm-btn" @click.stop="selecionarCustomer(c)">
                  <i class="fas fa-arrow-right"></i>
                </button>
              </template>
            </div>
          </div>

          <!-- Barra de ações em modo edição -->
          <transition name="fade-bar">
            <div v-if="editMode" class="edit-action-bar">
              <button
                class="btn btn-danger btn-sm rounded-3 w-100 py-2"
                :disabled="selectedCpfs.length === 0"
                @click="showDeleteModal = true"
              >
                <i class="fas fa-trash me-2"></i>
                Excluir{{ selectedCpfs.length > 0 ? ` (${selectedCpfs.length})` : '' }}
              </button>
            </div>
          </transition>

          <div class="text-center mt-3 mb-0">
            <a href="#" class="link-switch" @click.prevent="showNewForm = true">
              <i class="fas fa-user-plus me-1"></i>Usar outros dados nessa compra
            </a>
          </div>

        </div>

        <!-- Formulário novo cliente -->
        <div v-else>
          <div class="step-header mb-4">
            <h2 class="step-title">Seus dados</h2>
            <p class="step-subtitle">Precisamos identificar o presenteador 🎁</p>
          </div>

          <div class="identity-form-card">
            <div class="row gx-2">
              <div class="col-12 col-md-6 mb-3">
                <CpfCnpjInput
                  id="payment-cpf-cnpj"
                  v-model="form.cpf_cnpj"
                  :disabled="lookingUp || saving"
                  :error="(touched.cpf_cnpj && errors.cpf_cnpj) || gatewayFieldErrors.cpf_cnpj || ''"
                  @type-detected="handleDocumentTypeDetected"
                  @update:modelValue="gatewayFieldErrors.cpf_cnpj = ''"
                />
              </div>

              <div class="col-12 col-md-6 mb-3">
                <label class="payment-form-label">Nome completo</label>
                <input
                  id="payment-name"
                  v-model="form.name"
                  type="text"
                  :class="['form-control payment-form-control', { 'is-invalid': (touched.name && errors.name) || gatewayFieldErrors.name }]"
                  placeholder="Seu nome"
                  :disabled="saving"
                  @blur="touched.name = true"
                  @input="gatewayFieldErrors.name = ''"
                />
                <div v-if="(touched.name && errors.name) || gatewayFieldErrors.name" class="invalid-feedback d-block">{{ errors.name || gatewayFieldErrors.name }}</div>
              </div>
            </div>

            <div class="row gx-2">
              <div class="col-12 col-md-6 mb-3">
                <label class="payment-form-label">E-mail</label>
                <input
                  id="payment-email"
                  v-model="form.email"
                  type="email"
                  :class="['form-control payment-form-control', { 'is-invalid': (touched.email && errors.email) || gatewayFieldErrors.email }]"
                  placeholder="seu@email.com"
                  :disabled="saving"
                  @blur="touched.email = true"
                  @input="gatewayFieldErrors.email = ''"
                />
                <div v-if="(touched.email && errors.email) || gatewayFieldErrors.email" class="invalid-feedback d-block">{{ errors.email || gatewayFieldErrors.email }}</div>
              </div>

              <div class="col-12 col-md-6 mb-3">
                <label class="payment-form-label">Telefone (WhatsApp)</label>
                <input
                  id="payment-phone"
                  v-model="form.phone"
                  v-mask="'(##) #####-####'"
                  type="text"
                  inputmode="numeric"
                  :class="['form-control payment-form-control', { 'is-invalid': (touched.phone && errors.phone) || gatewayFieldErrors.phone }]"
                  placeholder="(00) 00000-0000"
                  :disabled="saving"
                  @blur="touched.phone = true"
                  @input="gatewayFieldErrors.phone = ''"
                />
                <div v-if="(touched.phone && errors.phone) || gatewayFieldErrors.phone" class="invalid-feedback d-block">{{ errors.phone || gatewayFieldErrors.phone }}</div>
              </div>
            </div>

            <template v-if="method === 'card'">
              <div class="row gx-2">
                <div class="col-12 col-md-6 mb-3">
                  <label class="payment-form-label">CEP</label>
                  <div class="position-relative">
                    <input
                      id="payment-postal-code"
                      v-model="form.postal_code"
                      v-mask="'#####-###'"
                      inputmode="numeric"
                      :class="['form-control payment-form-control',
                        { 'is-invalid': (touched.postal_code && errors.postal_code) || gatewayFieldErrors.postal_code }]"
                      placeholder="00000-000"
                      :disabled="saving"
                      @input="onCepInputView(); gatewayFieldErrors.postal_code = ''"
                      @blur="touched.postal_code = true"
                    />
                    <div v-if="isLoadingCep"
                      class="position-absolute top-50 end-0 translate-middle-y me-2">
                      <span class="spinner-border spinner-border-sm text-secondary"
                        role="status"></span>
                    </div>
                  </div>
                  <div v-if="(touched.postal_code && errors.postal_code) || gatewayFieldErrors.postal_code"
                    class="invalid-feedback d-block">
                    {{ errors.postal_code || gatewayFieldErrors.postal_code }}
                  </div>
                </div>

                <div class="col-12 col-md-6 mb-3">
                  <label class="payment-form-label">Número</label>
                  <input
                    id="payment-address-number"
                    v-model="form.address_number"
                    :class="['form-control payment-form-control',
                      { 'is-invalid': touched.address_number && errors.address_number }]"
                    placeholder="Ex: 123"
                    :disabled="saving"
                    @blur="touched.address_number = true"
                  />
                  <div v-if="touched.address_number && errors.address_number"
                    class="invalid-feedback d-block">
                    {{ errors.address_number }}
                  </div>
                </div>
              </div>
            </template>
          </div>

          <p v-if="lookingUp" class="text-muted small mt-1">
            <span class="spinner-border spinner-border-sm me-1"></span> Buscando seus dados...
          </p>

          <div class="disclaimer-box mt-4 mb-2" :class="{ 'disclaimer-box--error': disclaimerValidationError }">
            <div class="d-flex align-items-start gap-2">
              <input
                id="terms-checkout"
                v-model="checkoutDisclaimerAccepted"
                type="checkbox"
                class="form-check-input mt-1 flex-shrink-0"
                @change="disclaimerValidationError = false"
              />
              <label for="terms-checkout" class="form-check-label small text-muted">
                <strong>Importante:</strong>
                A QueMimo é uma plataforma tecnológica que facilita a gestão de presentes.
                Os valores são transferidos ao organizador do evento, que possui total responsabilidade
                pela sua utilização e pela realização do evento.
              </label>
            </div>
          </div>
        </div>

      </div>


      <!-- ========================
           STEP 2 — PIX
           ======================== -->
      <div v-show="currentStep === 2 && method === 'pix'">

        <!-- Gerando PIX -->
        <div v-if="pixFlow === 'generating'" class="text-center py-5">
          <div class="spinner-border text-pink mb-3" role="status"></div>
          <p class="text-muted">Gerando chave Pix...</p>
        </div>

        <!-- QR Code -->
        <div v-if="pixFlow === 'paying'" class="pix-paying-wrapper">

          <!-- Cabeçalho valor + crachá seguro -->
          <div class="pix-header">
            <div class="pix-secure-badge">
              <i class="fas fa-lock me-1"></i> Ambiente seguro
            </div>
            <div class="pix-amount-block">
              <span class="pix-amount-label">Valor a pagar</span>
              <span class="pix-amount-value">{{ totalFormatado }}</span>
            </div>
          </div>

          <!-- QR Code -->
          <div class="qr-code-wrapper">
            <img
              :src="`data:image/png;base64,${pixData.qrcode_image}`"
              alt="QR Code Pix"
              class="qr-code"
            />
            <p class="qr-hint"><i class="fas fa-mobile-screen-button me-1"></i>Escaneie com seu app bancário</p>
          </div>

          <!-- Divisor -->
          <div class="pix-or-divider"><span>ou copie a chave</span></div>

          <!-- Copiar chave -->
          <button class="pix-copy-btn" @click="copiarPix">
            <span class="pix-key-text">{{ pixData.pix_key }}</span>
            <span class="pix-copy-label flex-shrink-0">
              <i class="fas fa-copy me-1"></i>Copiar
            </span>
          </button>

          <!-- Countdown bonito -->
          <div class="pix-countdown">
            <div class="pix-countdown-bar">
              <div
                class="pix-countdown-fill"
                :style="{ width: `${((POLLING_INTERVAL - pollingCountdown) / POLLING_INTERVAL) * 100}%` }"
              ></div>
            </div>
            <div class="pix-countdown-text">
              <span class="pix-countdown-dot"></span>
              <span class="text-muted small">
                Aguardando pagamento — verificando em
                <strong class="pix-countdown-seconds">{{ pollingCountdown }}s</strong>
              </span>
            </div>
          </div>

        </div>

        <!-- Erro PIX -->
        <div v-if="pixFlow === 'error'" class="text-center py-5">
          <i class="fa-solid fa-circle-xmark text-danger mb-3" style="font-size: 3rem;"></i>

          <!-- Erro de limite / indisponibilidade temporária -->
          <template v-if="pixErrorDetail?.action === 'contact_support'">
            <h5 class="text-danger">Pix indisponível no momento</h5>
            <p class="text-muted px-3">
              {{ pixErrorDetail.message || 'Não foi possível processar o pagamento via Pix neste momento.' }}
            </p>
            <p class="text-muted small px-3">
              Nosso time já foi notificado. Você pode tentar novamente mais tarde ou pagar com cartão.
            </p>
            <div class="d-flex flex-column align-items-center gap-2 mt-3">
              <button class="btn btn-outline-secondary" @click="switchToCard">
                <i class="fa-solid fa-credit-card me-1"></i> Pagar com cartão
              </button>
              <button class="btn btn-link text-muted btn-sm" @click="goBackToCheckout">
                Voltar para escolher outro método
              </button>
            </div>
          </template>

          <!-- Erro genérico -->
          <template v-else>
            <h5 class="text-danger">Erro ao gerar Pix</h5>
            <p class="text-muted">{{ pixErrorDetail?.message || 'Não foi possível gerar a chave Pix. Tente novamente.' }}</p>
            <button class="btn btn-color-site mt-2" @click="gerarPix">Tentar novamente</button>
          </template>
        </div>

        <!-- Expirado -->
        <div v-if="pixFlow === 'expired'" class="text-center py-5">
          <i class="fa-solid fa-clock text-warning mb-3" style="font-size: 3rem;"></i>
          <h5 class="text-warning">Pix expirado</h5>
          <p class="text-muted">A validade do Pix expirou. Gere um novo para continuar.</p>
          <button class="btn btn-color-site mt-2" @click="gerarPix">Gerar novo Pix</button>
        </div>

      </div>

      <!-- ========================
           STEP 2 — CARTÃO
           ======================== -->
      <div v-show="currentStep === 2 && method === 'card'" class="checkout-card-step">
        <div class="step-header mb-4">
          <h2 class="step-title">Cartão de crédito</h2>
          <p class="step-subtitle">{{ cardStepSubtitle }}</p>
        </div>

        <div v-if="!isStripeCardProcessor" class="card-preview-section mb-4">
          <div class="card-preview" :class="{ 'is-back': cardPreviewSide === 'back' }">
            <div class="card-preview__face card-preview__face--front">
              <div class="card-preview__top">
                <span class="card-preview__chip"></span>
                <span class="card-preview__brand">{{ detectedCardBrand }}</span>
              </div>
              <p class="card-preview__number">{{ cardPreviewNumber }}</p>
              <div class="card-preview__bottom">
                <div>
                  <span class="card-preview__label">Nome no cartão</span>
                  <p class="card-preview__value">{{ cardPreviewName }}</p>
                </div>
                <div class="text-end">
                  <span class="card-preview__label">Validade</span>
                  <p class="card-preview__value">{{ cardPreviewExpiry }}</p>
                </div>
              </div>
            </div>

            <div class="card-preview__face card-preview__face--back">
              <div class="card-preview__stripe"></div>
              <div class="card-preview__cvv-wrap">
                <span class="card-preview__label card-preview__label--dark">CVV</span>
                <div class="card-preview__cvv-box">{{ cardPreviewCvv }}</div>
              </div>
              <div class="card-preview__back-footer">{{ cardPreviewName }}</div>
            </div>
          </div>
        </div>

        <form @submit.prevent="handleCardPrimaryAction">
          <template v-if="isStripeCardProcessor">
            <div class="mb-3">
              <label class="form-label fw-semibold">Nome no Cartão</label>
              <input
                v-model="cardForm.name"
                type="text"
                class="form-control payment-form-control"
                placeholder="Como está no cartão"
                :disabled="sendingCard"
              />
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">Dados do Cartão</label>
              <div class="stripe-card-element-wrap">
                <div id="stripe-card-element" class="stripe-card-element"></div>
              </div>
              <div v-if="stripeCardError" class="invalid-feedback d-block mt-1">
                {{ stripeCardError }}
              </div>
            </div>

            <div class="mb-3 installment-field">
              <label class="form-label fw-semibold">Parcelamento</label>
              <InstallmentCalculator
                :bin="cardBin"
                :total="totalNumber"
                :disabled="sendingCard"
                :allow-without-bin="true"
                @update:selected-installment="inst => selectedInstallment = inst"
                :initial-selected-installment="selectedInstallment"
              />
            </div>
          </template>

          <template v-else-if="!isMobileDevice">
            <div v-show="currentCardField === 'numberExpiry'" class="mb-3">
              <label class="form-label fw-semibold">Número do Cartão + Vencimento</label>
              <div class="card-step-field-main">
                <div class="card-number-inline">
                  <input
                    v-model="cardForm.number"
                    type="text"
                    :class="['form-control payment-form-control card-number-inline__number', { 'is-invalid': cardGatewayErrors.card_number }]"
                    placeholder="0000 0000 0000 0000"
                    v-mask="'#### #### #### #### ####'"
                    :disabled="sendingCard"
                    inputmode="numeric"
                    @focus="setCardField('numberExpiry')"
                    @input="cardGatewayErrors.card_number = ''"
                  />

                  <div class="card-number-inline__month">
                    <select
                      v-model="cardForm.expiryMonth"
                      :class="['form-select payment-form-control', { 'is-invalid': cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year }]"
                      :disabled="sendingCard"
                      @focus="setCardField('numberExpiry')"
                      @change="cardGatewayErrors.card_expiry_month = ''; cardGatewayErrors.card_expiry_year = ''"
                    >
                      <option value="">Mês</option>
                      <option
                        v-for="month in monthOptions"
                        :key="month.value"
                        :value="month.value"
                        :disabled="month.disabled"
                      >
                        {{ month.label }}
                      </option>
                    </select>
                  </div>

                  <div class="card-number-inline__year">
                    <select
                      v-model="cardForm.expiryYear"
                      :class="['form-select payment-form-control', { 'is-invalid': cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year }]"
                      :disabled="sendingCard"
                      @focus="setCardField('numberExpiry')"
                      @change="cardGatewayErrors.card_expiry_month = ''; cardGatewayErrors.card_expiry_year = ''"
                    >
                      <option value="">Ano</option>
                      <option v-for="year in yearOptions" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-if="cardGatewayErrors.card_number || cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year" class="invalid-feedback d-block mt-1">
                  {{ cardGatewayErrors.card_number || cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year }}
                </div>
              </div>
            </div>

            <div v-show="currentCardField === 'name'" class="mb-3">
              <label class="form-label fw-semibold">Nome no Cartão</label>
              <div class="card-step-field-main">
                <input
                  v-model="cardForm.name"
                  type="text"
                  :class="['form-control payment-form-control', { 'is-invalid': cardGatewayErrors.card_name }]"
                  placeholder="Como está no cartão"
                  :disabled="sendingCard"
                  @focus="setCardField('name')"
                  @input="cardGatewayErrors.card_name = ''"
                />
                <div v-if="cardGatewayErrors.card_name" class="invalid-feedback d-block mt-1">
                  {{ cardGatewayErrors.card_name }}
                </div>
              </div>
            </div>

            <div v-show="currentCardField === 'cvv'" class="mb-3">
              <label class="form-label fw-semibold">CVV</label>
              <div class="card-step-field-main">
                <input
                  v-model="cardForm.cvv"
                  type="text"
                  :class="['form-control payment-form-control', { 'is-invalid': cardGatewayErrors.card_cvv }]"
                  v-mask="'####'"
                  :disabled="sendingCard"
                  inputmode="numeric"
                  @focus="setCardField('cvv')"
                  @input="cardGatewayErrors.card_cvv = ''"
                />
                <div v-if="cardGatewayErrors.card_cvv" class="invalid-feedback d-block mt-1">
                  {{ cardGatewayErrors.card_cvv }}
                </div>
              </div>
            </div>

            <div v-show="currentCardField === 'installment'" class="mb-3">
              <label class="form-label fw-semibold">Parcelamento</label>
              <div class="card-step-field-main installment-field">
                <InstallmentCalculator
                  :bin="cardBin"
                  :total="totalNumber"
                  :disabled="sendingCard"
                  @update:selected-installment="inst => selectedInstallment = inst"
                  :initial-selected-installment="selectedInstallment"
                />
              </div>
            </div>
          </template>

          <template v-else>
            <div class="mb-3">
              <label class="form-label fw-semibold">Número do Cartão</label>
              <input
                v-model="cardForm.number"
                type="text"
                :class="['form-control payment-form-control', { 'is-invalid': cardGatewayErrors.card_number }]"
                placeholder="0000 0000 0000 0000"
                v-mask="'#### #### #### #### ####'"
                :disabled="sendingCard"
                inputmode="numeric"
                @focus="setCardField('numberExpiry')"
                @input="cardGatewayErrors.card_number = ''"
              />
              <div v-if="cardGatewayErrors.card_number" class="invalid-feedback d-block mt-1">
                {{ cardGatewayErrors.card_number }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">Vencimento</label>
              <div class="row gx-2">
                <div class="col-6">
                  <select
                    v-model="cardForm.expiryMonth"
                    :class="['form-select payment-form-control', { 'is-invalid': cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year }]"
                    :disabled="sendingCard"
                    @focus="setCardField('numberExpiry')"
                    @change="cardGatewayErrors.card_expiry_month = ''; cardGatewayErrors.card_expiry_year = ''"
                  >
                    <option value="">Mês</option>
                    <option
                      v-for="month in monthOptions"
                      :key="month.value"
                      :value="month.value"
                      :disabled="month.disabled"
                    >
                      {{ month.label }}
                    </option>
                  </select>
                </div>
                <div class="col-6">
                  <select
                    v-model="cardForm.expiryYear"
                    :class="['form-select payment-form-control', { 'is-invalid': cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year }]"
                    :disabled="sendingCard"
                    @focus="setCardField('numberExpiry')"
                    @change="cardGatewayErrors.card_expiry_month = ''; cardGatewayErrors.card_expiry_year = ''"
                  >
                    <option value="">Ano</option>
                    <option v-for="year in yearOptions" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </div>
              </div>
              <div v-if="cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year" class="invalid-feedback d-block mt-1">
                {{ cardGatewayErrors.card_expiry_month || cardGatewayErrors.card_expiry_year }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">Nome no Cartão</label>
              <input
                v-model="cardForm.name"
                type="text"
                :class="['form-control payment-form-control', { 'is-invalid': cardGatewayErrors.card_name }]"
                placeholder="Como está no cartão"
                :disabled="sendingCard"
                @focus="setCardField('name')"
                @input="cardGatewayErrors.card_name = ''"
              />
              <div v-if="cardGatewayErrors.card_name" class="invalid-feedback d-block mt-1">
                {{ cardGatewayErrors.card_name }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">CVV</label>
              <input
                v-model="cardForm.cvv"
                type="text"
                :class="['form-control payment-form-control', { 'is-invalid': cardGatewayErrors.card_cvv }]"
                v-mask="'####'"
                :disabled="sendingCard"
                inputmode="numeric"
                @focus="setCardField('cvv')"
                @input="cardGatewayErrors.card_cvv = ''"
              />
              <div v-if="cardGatewayErrors.card_cvv" class="invalid-feedback d-block mt-1">
                {{ cardGatewayErrors.card_cvv }}
              </div>
            </div>

            <div class="mb-3 installment-field">
              <label class="form-label fw-semibold">Parcelamento</label>
              <InstallmentCalculator
                :bin="cardBin"
                :total="totalNumber"
                :disabled="sendingCard"
                @update:selected-installment="inst => selectedInstallment = inst"
                :initial-selected-installment="selectedInstallment"
              />
            </div>
          </template>
        </form>

        <!-- Processando -->
        <div v-if="cardFlow === 'processing'" class="text-center py-4">
          <div class="spinner-border text-pink mb-3" role="status"></div>
          <p class="text-muted">Processando pagamento...</p>
        </div>

      </div>

      </div>
    </main>

    <!-- Footer de Navegação -->
    <footer v-if="!paymentSucceeded" class="app-footer">
      <div class="footer-inner">
      <!-- Voltar: oculto quando QR Code PIX está ativo -->
      <!-- No desktop step 1 fica desabilitado (igual ao BankDetails); mobile step 1 chama router.back() -->
      <button
        v-if="!(currentStep === 2 && method === 'pix' && pixFlow === 'paying')"
        class="btn btn-outline-secondary btn-nav"
        @click="voltar"
        :disabled="saving || sendingCard || (currentStep === 2 && method === 'pix' && pixFlow === 'generating')"
      >
        <i class="fas fa-chevron-left me-2"></i> Voltar
      </button>

      <!-- Step 1 → Avançar (apenas quando formulário está visível) -->
      <button
        v-if="currentStep === 1 && (!customerAlreadySaved || showNewForm)"
        class="btn btn-color-site btn-nav"
        @click="avancar"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
        {{ method === 'pix' ? 'Gerar QR Code Pix' : 'Avançar' }}
        <i v-if="!saving" class="fas fa-arrow-right ms-2"></i>
      </button>

      <!-- Step 2 Cartão → Pagar -->
      <button
        v-if="currentStep === 2 && method === 'card' && cardFlow === 'form'"
        class="btn btn-nav btn-pay-highlight"
        @click="handleCardPrimaryAction"
        :disabled="isStripeCardProcessor ? (sendingCard || !isStripeFormValid) : (sendingCard || (currentCardField === 'installment' ? !isCardFormValid : !canAdvanceCurrentCardField))"
      >
        <span v-if="sendingCard" class="spinner-border spinner-border-sm me-2"></span>
        <span v-else class="btn-pay-highlight__label">
          <i :class="(isStripeCardProcessor || currentCardField === 'installment') ? 'fas fa-lock me-2' : 'fas fa-chevron-right me-2'"></i>
          {{ (isStripeCardProcessor || currentCardField === 'installment') ? `Finalizar pagamento • R$ ${totalParaPagar}` : 'Continuar' }}
        </span>
      </button>
      </div>
    </footer>

    <FullScreenLoader v-if="loading" :message="loadingMessage" />

    <PaymentSuccessModal
      v-if="showSuccessModal"
      :show="showSuccessModal"
      :event-id="eventId"
      :event-share-url="eventShareUrl"
      @close="showSuccessModal = false"
      @redirect-event="handleSuccessRedirect"
    />

    <BottomModal :visible="showCompleteProfileModal" @close="fecharCompleteModal">
      <template #default>
        <div class="complete-profile-modal">
          <div class="complete-modal-header">
            <div class="complete-modal-avatar">
              {{ customerParaCompletar?.name?.charAt(0)?.toUpperCase() }}
            </div>
            <div>
              <h5 class="complete-modal-title">
                Olá, {{ customerParaCompletar?.name?.split(' ')[0] }}!
              </h5>
              <p class="complete-modal-sub">
                Para pagar com cartão precisamos do seu endereço. É rápido! 📦
              </p>
            </div>
          </div>

          <div class="complete-modal-fields">
            <div class="mb-3">
              <label class="form-label fw-semibold">
                CEP <span class="text-danger">*</span>
              </label>
              <div class="position-relative">
                <input
                  id="complete-postal-code"
                  v-model="completingForm.postal_code"
                  class="form-control"
                  placeholder="00000-000"
                  v-mask="'#####-###'"
                  inputmode="numeric"
                  :disabled="savingComplete"
                  @input="onCepCompleteInput"
                />
                <div
                  v-if="isLoadingCepComplete"
                  class="position-absolute top-50 end-0 translate-middle-y me-2"
                >
                  <span class="spinner-border spinner-border-sm text-secondary" role="status"></span>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label fw-semibold">
                Número do endereço <span class="text-danger">*</span>
              </label>
              <input
                id="complete-address-number"
                v-model="completingForm.address_number"
                class="form-control"
                placeholder="Ex: 123"
                :disabled="savingComplete"
              />
            </div>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary w-100"
              @click="fecharCompleteModal"
              :disabled="savingComplete"
            >
              Cancelar
            </button>
            <button
              class="btn btn-color-site w-100"
              @click="salvarDadosCompletos"
              :disabled="!isCompleteFormValid || savingComplete"
            >
              <span v-if="savingComplete">
                <span class="spinner-border spinner-border-sm me-1"></span> Salvando...
              </span>
              <span v-else>
                <i class="fas fa-arrow-right me-2"></i> Continuar
              </span>
            </button>
          </div>
        </div>
      </template>
    </BottomModal>

    <CardDeclinedModal
      :show="showCardDeclinedModal"
      @try-again="handleDeclinedTryAgain"
      @use-pix="handleDeclinedUsePix"
    />

    <!-- Guard: Pagamento em andamento -->
    <BottomModal :visible="showLeaveModal" @close="cancelarSaida">
      <div class="text-center p-3">
        <i class="fas fa-clock text-warning mb-3" style="font-size: 2.5rem; display: block;"></i>
        <h5 class="fw-bold mb-2">Pagamento em andamento</h5>
        <p class="text-muted mb-4">
          Seu QR Code Pix ainda está ativo. Se você sair agora, precisará gerar um novo link. Deseja aguardar?
        </p>
        <button class="btn btn-color-site w-100 mb-2 py-3" @click="cancelarSaida">
          <i class="fas fa-clock me-2"></i> Aguardar pagamento
        </button>
        <button class="btn btn-outline-secondary w-100 py-2" @click="confirmarSaida">
          Sair mesmo assim
        </button>
      </div>
    </BottomModal>

    <!-- Confirmar exclusão de customers -->
    <BottomModal :visible="showDeleteModal" @close="showDeleteModal = false">
      <div class="text-center p-3">
        <i class="fas fa-trash text-danger mb-3" style="font-size: 2rem; display: block;"></i>
        <h5 class="fw-bold mb-1">Excluir perfis salvos</h5>
        <p class="text-muted mb-4">
          Você tem certeza que deseja remover
          <strong>{{ selectedCpfs.length }} perfil{{ selectedCpfs.length > 1 ? 'is' : '' }}</strong>
          dos dados salvos neste dispositivo?
        </p>
        <button class="btn btn-danger w-100 mb-2 py-3" @click="confirmarExclusao">
          <i class="fas fa-trash me-2"></i>Sim, excluir
        </button>
        <button class="btn btn-outline-secondary w-100 py-2" @click="showDeleteModal = false">
          Cancelar
        </button>
      </div>
    </BottomModal>

    <BottomModal
      :visible="showPixCustomerConfirmModal"
      @close="cancelarConfirmacaoPixCustomer"
      :fullHeight="false"
    >
      <template #default>
        <div class="p-3 text-center">
          <h5 class="fw-bold mb-2">Gerar QR Code Pix</h5>
          <p class="text-muted mb-4">
            Vamos gerar um QR Code Pix para
            <strong>{{ pendingPixCustomer?.name }}</strong>.
            Deseja continuar?
          </p>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary w-100"
              @click="cancelarConfirmacaoPixCustomer"
            >
              Cancelar
            </button>
            <button
              class="btn btn-color-site w-100"
              @click="confirmarPixCustomerSelection"
            >
              Gerar QR Code
            </button>
          </div>
        </div>
      </template>
    </BottomModal>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { toast } from 'vue3-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { formatCurrency } from '@/utils/currencyFormatter';

import CpfCnpjInput from '@/components/CpfCnpjInput.vue';
import InstallmentCalculator from '@/components/payment/InstallmentCalculator.vue';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import PaymentSuccessModal from '@/components/payment/PaymentSuccessModal.vue';
import CardDeclinedModal from '@/components/payment/CardDeclinedModal.vue';
import { isPaymentTerminal } from '@/utils/paymentStatus';
import BottomModal from '@/components/BottomModal.vue';

import { useCartStore } from '@/stores/cartStore';
import { useEventTheme } from '@/composables/useEventTheme';
import { useCustomerStore } from '@/stores/customerStore';
import { lookupGuest, createGuest } from '@/services/guestService';
import {
  hasGatewayErrors,
  extractGatewayErrors,
  fieldErrorsToMap,
  normalizeValidationErrors,
} from '@/utils/gatewayErrorHandler';
import api from '@/services/api';
import { logAcceptance } from '@/services/complianceService';

// ─────────────────────────────────────────────
// Router / route
// ─────────────────────────────────────────────
const route  = useRoute();
const router = useRouter();
const eventId = route.params.id;
const { isReady: themeReady } = useEventTheme(eventId, { cleanup: true });
const themeLoading = ref(true);
watch(themeReady, (ready) => {
  if (ready) setTimeout(() => { themeLoading.value = false }, 200);
}, { immediate: true });
const method  = route.query.ref || 'pix'; // 'pix' | 'card'
const cardProcessor = ref('asaas');
const stripePublishableKey = ref('');
const stripeClient = ref(null);
const isStripeCardProcessor = computed(() => method === 'card' && cardProcessor.value === 'stripe');
const stripeElements = ref(null);
const stripeCardElement = ref(null);
const stripeCardComplete = ref(false);
const stripeCardError = ref('');

// ─────────────────────────────────────────────
// Stores
// ─────────────────────────────────────────────
const cart         = useCartStore();
const customerStore = useCustomerStore();

const items       = computed(() => cart.getEvent(eventId)?.items || []);
const totalNumber = computed(() => items.value.reduce((sum, i) => sum + i.price * i.quantity, 0));

// ─────────────────────────────────────────────
// Steps
// ─────────────────────────────────────────────
const currentStep    = ref(1);
const _mq            = window.matchMedia('(max-width: 767.98px)');
const isMobileDevice = ref(_mq.matches);
const _mqHandler     = (e) => { isMobileDevice.value = e.matches; };
_mq.addEventListener('change', _mqHandler);

// Estado de pagamento concluído (bloqueia footer e guard de saída)
const paymentSucceeded    = ref(false);
const showLeaveModal      = ref(false);
const leaveTarget         = ref('route'); // 'route' | 'step1'
let   pendingLeaveResolve = null;

// Computed de boas-vindas e formatado
const customerAlreadySaved = computed(() => customerStore.hasCustomers);
const totalFormatado       = computed(() =>
  totalNumber.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
);

// Gerenciamento da lista de customers
const editMode       = ref(false);
const selectedCpfs   = ref([]);
const showNewForm    = ref(false);
const showDeleteModal = ref(false);
const showPixCustomerConfirmModal = ref(false);
const pendingPixCustomer = ref(null);
const checkoutDisclaimerAccepted = ref(false);
const disclaimerValidationError = ref(false);

function toggleEditMode() {
  editMode.value     = !editMode.value;
  selectedCpfs.value = [];
}

function toggleSelectCpf(cpf) {
  const idx = selectedCpfs.value.indexOf(cpf);
  if (idx >= 0) selectedCpfs.value.splice(idx, 1);
  else selectedCpfs.value.push(cpf);
}

function confirmarExclusao() {
  customerStore.removeCustomers(selectedCpfs.value);
  selectedCpfs.value  = [];
  editMode.value      = false;
  showDeleteModal.value = false;
  // Se a lista ficou vazia, mostrar formulário
  if (!customerStore.hasCustomers) showNewForm.value = true;
}

const steps = [
  { id: 1, name: 'Identificação', icon: 'fa-solid fa-user' },
  { id: 2, name: method === 'pix' ? 'Pix' : 'Cartão', icon: method === 'pix' ? 'fa-solid fa-qrcode' : 'fa-solid fa-credit-card' },
];

// ─────────────────────────────────────────────
// Step 1 — Identificação
// ─────────────────────────────────────────────
const lookingUp = ref(false);
const saving    = ref(false);
const isLoadingCep = ref(false);
const gatewayFieldErrors = ref({});
const showCompleteProfileModal = ref(false);
const customerParaCompletar = ref(null);
const completingForm = reactive({
  postal_code: '',
  address_number: '',
});
const savingComplete = ref(false);
const isLoadingCepComplete = ref(false);
let   cepAbortCtrl = null;
let   cepCompleteAbortCtrl = null;
let   cepLookupTimer = null;
let   cepCompleteLookupTimer = null;
let   lastMainCepRequested = '';
let   lastCompleteCepRequested = '';
const CEP_LOOKUP_DEBOUNCE_MS = 250;
const CEP_NOT_FOUND_TOAST_ID = 'payment-cep-not-found';

const form = reactive({
  cpf_cnpj:       '',
  name:           '',
  email:          '',
  phone:          '',
  postal_code:    '',
  address_number: '',
});

const cpfDigits = computed(() => form.cpf_cnpj.replace(/\D/g, ''));
const cpfMask   = computed(() => cpfDigits.value.length <= 11 ? '###.###.###-##' : '##.###.###/####-##');
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const touched = reactive({
  cpf_cnpj:       false,
  name:           false,
  email:          false,
  phone:          false,
  postal_code:    false,
  address_number: false,
});

const errors = computed(() => ({
  cpf_cnpj: (cpfDigits.value.length !== 11 && cpfDigits.value.length !== 14)
    ? 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido'
    : null,
  name: form.name.trim().length < 3
    ? 'Informe seu nome completo'
    : null,
  email: !emailRegex.test(form.email.trim())
    ? 'Informe um e-mail válido (ex: nome@dominio.com).'
    : null,
  phone: form.phone.replace(/\D/g, '').length < 10
    ? 'Informe um telefone válido'
    : null,
  postal_code: method === 'card' && form.postal_code.replace(/\D/g, '').length !== 8
    ? 'Informe um CEP válido com 8 dígitos'
    : null,
  address_number: method === 'card' && !form.address_number.trim()
    ? 'Informe o número do endereço'
    : null,
}));

const isFormValid = computed(() => {
  const base = !errors.value.cpf_cnpj &&
    !errors.value.name &&
    !errors.value.email &&
    !errors.value.phone;

  if (method === 'card') {
    return base && !errors.value.postal_code && !errors.value.address_number;
  }
  return base;
});

async function fetchAddressByCepView(cep) {
  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length !== 8) return;

  if (isLoadingCep.value && lastMainCepRequested === cleanCep) return;

  if (cepAbortCtrl) cepAbortCtrl.abort();
  cepAbortCtrl = new AbortController();
  const currentAbortCtrl = cepAbortCtrl;
  lastMainCepRequested = cleanCep;
  isLoadingCep.value = true;

  try {
    const response = await fetch(
      `https://brasilapi.com.br/api/cep/v1/${cleanCep}`,
      { signal: currentAbortCtrl.signal }
    );
    if (!response.ok) throw new Error('CEP não encontrado');
    await nextTick();
    document.getElementById('payment-address-number')?.focus();
  } catch (err) {
    if (err.name === 'AbortError') return;
    showCepNotFoundWarning();
  } finally {
    if (cepAbortCtrl === currentAbortCtrl) {
      isLoadingCep.value = false;
      cepAbortCtrl = null;
    }
  }
}

function onCepInputView() {
  if (cepLookupTimer) {
    clearTimeout(cepLookupTimer);
    cepLookupTimer = null;
  }

  const clean = form.postal_code.replace(/\D/g, '');
  if (clean.length !== 8) {
    lastMainCepRequested = '';
    if (cepAbortCtrl) {
      cepAbortCtrl.abort();
      cepAbortCtrl = null;
      isLoadingCep.value = false;
    }
    return;
  }

  cepLookupTimer = setTimeout(() => {
    fetchAddressByCepView(clean);
  }, CEP_LOOKUP_DEBOUNCE_MS);
}

async function handleCpfBlur() {
  const digits = cpfDigits.value;
  if (digits.length !== 11 && digits.length !== 14) return;
  lookingUp.value = true;
  try {
    const result = await lookupGuest(digits);
    if (result.found) {
      form.name  = result.name  || form.name;
      form.email = result.email || form.email;
      form.phone = result.phone || form.phone;
    }
  } catch {
    // silencioso — não bloqueia o fluxo
  } finally {
    lookingUp.value = false;
  }
}

function handleDocumentTypeDetected(type) {
  touched.cpf_cnpj = true;
  if (!type || lookingUp.value) return;
  handleCpfBlur();
}

async function avancar() {
  if (saving.value) return;

  // Marcar todos os campos como tocados para exibir erros
  touched.cpf_cnpj = true;
  touched.name = true;
  touched.email = true;
  touched.phone = true;
  touched.postal_code = true;
  touched.address_number = true;

  const validationOrder = [
    'cpf_cnpj',
    'name',
    'email',
    'phone',
    ...(method === 'card' ? ['postal_code', 'address_number'] : []),
  ];

  const firstInvalidField = validationOrder.find((field) => !!errors.value[field]);
  if (firstInvalidField) {
    toast.warning(errors.value[firstInvalidField], {
      position: 'top-right',
      autoClose: 3000,
    });
    await nextTick();
    focusPaymentField(firstInvalidField);
    return;
  }

  if (!checkoutDisclaimerAccepted.value) {
    disclaimerValidationError.value = true;
    toast.warning('É obrigatório aceitar o aviso para continuar.', {
      position: 'top-right',
      autoClose: 3500,
    });
    await nextTick();
    const termsInput = document.getElementById('terms-checkout');
    scrollFieldIntoView(termsInput);
    termsInput?.focus?.();
    return;
  }

  disclaimerValidationError.value = false;

  saving.value = true;
  try {
    const result = await createGuest({
      name:           form.name.trim(),
      cpf_cnpj:       cpfDigits.value,
      email:          form.email.trim(),
      phone:          form.phone.replace(/\D/g, ''),
      postal_code:    form.postal_code.replace(/\D/g, ''),
      address_number: form.address_number.trim(),
    });
    customerStore.addOrUpdateCustomer({
      id:                result.id,
      provider_customer_id: result.provider_customer_id || result.asaas_customer_id,
      name:              form.name.trim(),
      cpf_cnpj:          cpfDigits.value,
      email:             form.email.trim(),
      phone:             form.phone.replace(/\D/g, ''),
      postal_code:       form.postal_code.replace(/\D/g, ''),
      address_number:    form.address_number.trim(),
    });
    gatewayFieldErrors.value = {};
    currentStep.value = 2;

    try {
      const guestEmail = form.email?.trim() || null;
      await logAcceptance('checkout_disclaimer', guestEmail);
    } catch (e) {
      console.warn('[compliance] falha ao registrar aceite checkout_disclaimer', e);
    }

    if (method === 'pix') gerarPix();
  } catch (err) {
    if (hasGatewayErrors(err)) {
      const fieldErrors = extractGatewayErrors(err);
      gatewayFieldErrors.value = fieldErrorsToMap(fieldErrors);

      const firstError = fieldErrors[0];
      toast.error(firstError.message, { autoClose: 6000, position: 'top-right' });

      await nextTick();
      focusPaymentField(firstError.field);

      for (let i = 1; i < fieldErrors.length; i++) {
        setTimeout(() => {
          toast.error(fieldErrors[i].message, { autoClose: 6000, position: 'top-right' });
        }, i * 800);
      }
      return;
    }
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      'Erro ao salvar seus dados. Tente novamente.';
    toast.error(msg, { autoClose: 5000, position: 'top-right' });
  } finally {
    saving.value = false;
  }
}

function focusPaymentField(field) {
  const fieldIdMap = {
    cpf_cnpj: 'payment-cpf-cnpj',
    name: 'payment-name',
    email: 'payment-email',
    phone: 'payment-phone',
    postal_code: 'payment-postal-code',
    address_number: 'payment-address-number',
  };
  const id = fieldIdMap[field];
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;

  scrollFieldIntoView(el);

  if (typeof el.focus === 'function') {
    el.focus();
    return;
  }

  const focusable = el.querySelector?.('input, select, textarea');
  focusable?.focus();
}

function scrollFieldIntoView(element) {
  if (!element) return;

  const blockPosition = isMobileDevice.value ? 'start' : 'center';
  element.scrollIntoView({ behavior: 'smooth', block: blockPosition });

  if (isMobileDevice.value) {
    window.setTimeout(() => {
      window.scrollBy({ top: -120, behavior: 'smooth' });
    }, 120);
  }
}

async function focusElementByIdWithRetry(elementId, attempts = 5, delayMs = 120) {
  if (!elementId) return false;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await nextTick();

    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus?.();
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  return false;
}

function shouldOpenGifterEditModalForError(errorItem) {
  const action = String(errorItem?.action || '').toLowerCase();
  const field = errorItem?.field;

  if (action === 'edit_gifter_profile') return true;
  return field === 'postal_code' || field === 'address_number';
}

async function openGifterEditAndFocusField(errorItem) {
  const field = errorItem?.field === 'address_number' ? 'address_number' : 'postal_code';
  const activeCustomer = customerStore.activeCustomer || customerStore.customers[0] || null;

  currentStep.value = 1;

  if (!activeCustomer) {
    showNewForm.value = true;
    await focusElementByIdWithRetry(field === 'address_number' ? 'payment-address-number' : 'payment-postal-code');
    return;
  }

  showNewForm.value = false;
  customerParaCompletar.value = activeCustomer;
  completingForm.postal_code = activeCustomer.postal_code || '';
  completingForm.address_number = activeCustomer.address_number || '';
  showCompleteProfileModal.value = true;

  await focusElementByIdWithRetry(field === 'address_number' ? 'complete-address-number' : 'complete-postal-code');
}

function showUpdateGifterToast(onClick) {
  toast.info('Atualizar dados do presenteador', {
    autoClose: 5000,
    position: 'top-right',
    closeOnClick: true,
    onClick,
  });
}

function camposFaltantes(c) {
  const faltando = [];
  if (!c?.cpf_cnpj) faltando.push('cpf_cnpj');
  if (!c?.name?.trim()) faltando.push('name');
  if (!c?.email?.trim()) faltando.push('email');
  if (!c?.phone?.trim()) faltando.push('phone');

  if (method === 'card') {
    const cep = (c?.postal_code || '').replace(/\D/g, '');
    if (cep.length !== 8) faltando.push('postal_code');
    if (!c?.address_number?.trim()) faltando.push('address_number');
  }

  return faltando;
}

function customerCompleto(c) {
  return camposFaltantes(c).length === 0;
}

async function selecionarCustomer(c) {
  if (method === 'pix') {
    confirmarIdentidade(c);
    return;
  }

  if (!customerCompleto(c)) {
    completingForm.postal_code = c.postal_code || '';
    completingForm.address_number = c.address_number || '';
    customerParaCompletar.value = c;
    showCompleteProfileModal.value = true;
    return;
  }

  customerStore.setActiveCustomer(c);
  currentStep.value = 2;
}

async function fetchCepComplete(cep) {
  const clean = cep.replace(/\D/g, '');
  if (clean.length !== 8) return;

  if (isLoadingCepComplete.value && lastCompleteCepRequested === clean) return;

  if (cepCompleteAbortCtrl) cepCompleteAbortCtrl.abort();
  cepCompleteAbortCtrl = new AbortController();
  const currentAbortCtrl = cepCompleteAbortCtrl;
  lastCompleteCepRequested = clean;
  isLoadingCepComplete.value = true;

  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${clean}`, {
      signal: currentAbortCtrl.signal,
    });
    if (!response.ok) throw new Error('CEP não encontrado');
    await nextTick();
    document.getElementById('complete-address-number')?.focus();
  } catch (err) {
    if (err.name === 'AbortError') return;
    showCepNotFoundWarning();
  } finally {
    if (cepCompleteAbortCtrl === currentAbortCtrl) {
      isLoadingCepComplete.value = false;
      cepCompleteAbortCtrl = null;
    }
  }
}

function onCepCompleteInput() {
  if (cepCompleteLookupTimer) {
    clearTimeout(cepCompleteLookupTimer);
    cepCompleteLookupTimer = null;
  }

  const clean = completingForm.postal_code.replace(/\D/g, '');
  if (clean.length !== 8) {
    lastCompleteCepRequested = '';
    if (cepCompleteAbortCtrl) {
      cepCompleteAbortCtrl.abort();
      cepCompleteAbortCtrl = null;
      isLoadingCepComplete.value = false;
    }
    return;
  }

  cepCompleteLookupTimer = setTimeout(() => {
    fetchCepComplete(clean);
  }, CEP_LOOKUP_DEBOUNCE_MS);
}

function showCepNotFoundWarning() {
  if (toast.isActive?.(CEP_NOT_FOUND_TOAST_ID)) return;

  toast.warning('CEP não encontrado. Verifique e continue.', {
    toastId: CEP_NOT_FOUND_TOAST_ID,
    autoClose: 3000,
    position: 'top-right',
  });
}

const isCompleteFormValid = computed(() => {
  const cep = completingForm.postal_code.replace(/\D/g, '');
  return cep.length === 8 && completingForm.address_number.trim().length >= 1;
});

async function salvarDadosCompletos() {
  if (!isCompleteFormValid.value || !customerParaCompletar.value) return;
  savingComplete.value = true;

  try {
    const result = await createGuest({
      name: customerParaCompletar.value.name,
      cpf_cnpj: customerParaCompletar.value.cpf_cnpj,
      email: customerParaCompletar.value.email,
      phone: customerParaCompletar.value.phone || '',
      postal_code: completingForm.postal_code.replace(/\D/g, ''),
      address_number: completingForm.address_number.trim(),
    });

    const customerAtualizado = {
      ...customerParaCompletar.value,
      id: result?.id || customerParaCompletar.value.id,
      provider_customer_id:
        result?.provider_customer_id
        || result?.asaas_customer_id
        || customerParaCompletar.value.provider_customer_id
        || customerParaCompletar.value.asaas_customer_id,
      postal_code: completingForm.postal_code.replace(/\D/g, ''),
      address_number: completingForm.address_number.trim(),
    };

    customerStore.addOrUpdateCustomer(customerAtualizado);

    showCompleteProfileModal.value = false;
    customerParaCompletar.value = null;
    customerStore.setActiveCustomer(customerAtualizado);
    currentStep.value = 2;
  } catch (err) {
    if (hasGatewayErrors(err)) {
      const fieldErrors = extractGatewayErrors(err);
      toast.error(fieldErrors[0]?.message || 'Verifique os dados e tente novamente.', {
        autoClose: 5000,
        position: 'top-right',
      });
      return;
    }

    const msg = err?.response?.data?.message || 'Não foi possível salvar os dados. Tente novamente.';
    toast.error(msg, { autoClose: 5000, position: 'top-right' });
  } finally {
    savingComplete.value = false;
  }
}

function fecharCompleteModal() {
  showCompleteProfileModal.value = false;
  customerParaCompletar.value = null;
  completingForm.postal_code = '';
  completingForm.address_number = '';
}

// Confirmar identidade salva no storage — pula formulário
function confirmarIdentidade(customer) {
  if (method === 'pix') {
    pendingPixCustomer.value = customer;
    showPixCustomerConfirmModal.value = true;
    return;
  }

  customerStore.setActiveCustomer(customer);
  currentStep.value = 2;
  if (method === 'pix') gerarPix();
}

function cancelarConfirmacaoPixCustomer() {
  showPixCustomerConfirmModal.value = false;
  pendingPixCustomer.value = null;
}

function confirmarPixCustomerSelection() {
  if (!pendingPixCustomer.value) {
    cancelarConfirmacaoPixCustomer();
    return;
  }

  customerStore.setActiveCustomer(pendingPixCustomer.value);
  cancelarConfirmacaoPixCustomer();
  currentStep.value = 2;
  gerarPix();
}

// Usar outros dados nessa compra (sem tocar no storage)
function trocarIdentidade() {
  showNewForm.value = true;
}

// ─────────────────────────────────────────────
// Step 2 — PIX
// ─────────────────────────────────────────────
const POLLING_INTERVAL = 15;
const MAX_POLL_ATTEMPTS = 40; // 40 × 15s = 10 min
const pixFlow          = ref('generating'); // 'generating' | 'paying' | 'error' | 'expired'
const pixErrorDetail   = ref(null); // { action: 'contact_support', message: '...' } | null
const pixData          = reactive({ qrcode_image: '', pix_key: '', payment_id: '' });
const pollingCountdown = ref(POLLING_INTERVAL);
const isPollingActive  = ref(false);
let   pixInterval      = null;
let   countdownInterval = null;
let   pixPollAttempts  = 0;

function montarPayloadPix() {
  const c = customerStore.activeCustomer || customerStore.customers[0];
  return {
    event_id:          eventId,
    items:             items.value.map(i => ({ id: i.id, price: i.price, quantity: i.quantity })),
    total:             totalNumber.value,
    method:            'pix',
    customer_name:     c.name,
    customer_cpf_cnpj: c.cpf_cnpj,
    customer_email:    c.email,
    customer_phone:    c.phone,
  };
}

async function gerarPix() {
  pixFlow.value = 'generating';
  limparIntervalos();
  try {
    const { data } = await api.post(`/payments/pix/${eventId}`, montarPayloadPix());
    pixData.qrcode_image = data.qrcode_image;
    pixData.pix_key      = data.pix_key;
    pixData.payment_id   = data.id;

    customerStore.setPayment({
      event_id: eventId,
      method:   'pix',
      status:   'pending',
      data: {
        payment_id:   data.id,
        qrcode_image: data.qrcode_image,
        pix_key:      data.pix_key,
        expires_at:   data.expires_at || null,
      },
    });

    pixFlow.value = 'paying';
    iniciarPolling();
  } catch (err) {
    console.error('[PaymentView] Erro ao gerar PIX:', err);

    // Extrai contexto do erro do gateway para exibir mensagem adequada
    pixErrorDetail.value = null;
    const respData = err?.response?.data;
    if (respData) {
      const gatewayErrors = respData.gateway_errors || [];
      const contactAction = gatewayErrors.find((e) => e.action === 'contact_support');
      if (contactAction) {
        pixErrorDetail.value = {
          action: 'contact_support',
          message: contactAction.message || respData.message || '',
        };
      } else if (respData.message) {
        pixErrorDetail.value = { action: '', message: respData.message };
      }
    }

    toast.error(
      pixErrorDetail.value?.message || 'Não foi possível processar o pagamento. Tente novamente.'
    );
    pixFlow.value = 'error';
  }
}

function iniciarPolling() {
  limparIntervalos();
  pollingCountdown.value = POLLING_INTERVAL;
  isPollingActive.value  = true;
  pixPollAttempts        = 0;

  countdownInterval = setInterval(() => {
    pollingCountdown.value--;
  }, 1000);

  pixInterval = setInterval(verificarPagamentoPix, POLLING_INTERVAL * 1000);
}

async function verificarPagamentoPix() {
  pixPollAttempts++;
  if (pixPollAttempts >= MAX_POLL_ATTEMPTS) {
    limparIntervalos();
    customerStore.updatePaymentStatus('failed');
    pixFlow.value = 'expired';
    return;
  }
  isPollingActive.value = false;
  try {
    const { data } = await api.get(`/payments/status/${pixData.payment_id}`);
    if (data.status === 'paid') {
      limparIntervalos();
      customerStore.updatePaymentStatus('confirmed');
      mostrarSucesso();
    } else if (isPaymentTerminal(data.status) && data.status !== 'paid') {
      limparIntervalos();
      customerStore.updatePaymentStatus('failed');
      pixFlow.value = 'expired';
    }
  } catch (e) {
    console.error('[PaymentView] Erro ao verificar PIX:', e);
  } finally {
    pollingCountdown.value = POLLING_INTERVAL;
    isPollingActive.value  = true;
  }
}

function limparIntervalos() {
  if (pixInterval)      clearInterval(pixInterval);
  if (countdownInterval) clearInterval(countdownInterval);
  pixInterval       = null;
  countdownInterval = null;
}

function copiarPix() {
  const text = pixData.pix_key;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => toast.success('PIX copiado!'))
      .catch(() => copiarFallback(text));
  } else {
    copiarFallback(text);
  }
}

function copiarFallback(text) {
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity  = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    document.execCommand('copy');
    toast.success('PIX copiado!');
    document.body.removeChild(el);
  } catch {
    toast.error('Não foi possível copiar. Copie manualmente.');
  }
}

// ─────────────────────────────────────────────
// Step 2 — CARTÃO
// ─────────────────────────────────────────────
const cardFlow          = ref('form'); // 'form' | 'processing' | 'failed'
const sendingCard       = ref(false);
const selectedInstallment = ref(null);
const cardGatewayErrors = ref({});
const showCardDeclinedModal = ref(false);

const cardForm = reactive({ number: '', name: '', expiryMonth: '', expiryYear: '', cvv: '' });
const cardPreviewSide = ref('front');
const cardFieldOrder = ['numberExpiry', 'name', 'cvv', 'installment'];
const cardFieldIndex = ref(0);

// Pre-fill card holder name with buyer name from step 1
watch(currentStep, (step) => {
  if (step === 2 && method === 'card' && !cardForm.name) {
    const buyerName = customerStore.activeCustomer?.name || form.name || '';
    if (buyerName.trim()) cardForm.name = buyerName.trim();
  }
});

const currentCardField = computed(() => cardFieldOrder[cardFieldIndex.value]);

const monthOptions = computed(() => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const selectedYear = parseInt(cardForm.expiryYear, 10);
  const isCurrentYear = selectedYear === currentYear;

  return Array.from({ length: 12 }, (_, i) => {
    const monthNum = i + 1;
    const value = String(monthNum).padStart(2, '0');
    const isPast = isCurrentYear && monthNum < currentMonth;

    return {
      value,
      label: value,
      disabled: isPast,
    };
  });
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 15 }, (_, index) => String(currentYear + index));
});

const canGoPrevCardField = computed(() => cardFieldIndex.value > 0);
const canGoNextCardField = computed(() => cardFieldIndex.value < cardFieldOrder.length - 1);

const isNumberExpiryStepValid = computed(() =>
  cardForm.number.replace(/\D/g, '').length >= 15 &&
  !!cardForm.expiryMonth &&
  !!cardForm.expiryYear
);

const isNameStepValid = computed(() => cardForm.name.trim().length >= 3);
const isCvvStepValid = computed(() => cardForm.cvv.replace(/\D/g, '').length >= 3);

const canAdvanceCurrentCardField = computed(() => {
  if (currentCardField.value === 'numberExpiry') return isNumberExpiryStepValid.value;
  if (currentCardField.value === 'name') return isNameStepValid.value;
  if (currentCardField.value === 'cvv') return isCvvStepValid.value;
  return true;
});

function syncCardPreviewSideByField() {
  cardPreviewSide.value = currentCardField.value === 'cvv' ? 'back' : 'front';
}

function goPrevCardField() {
  if (!canGoPrevCardField.value) return;
  cardFieldIndex.value -= 1;
  syncCardPreviewSideByField();
}

function goNextCardField() {
  if (!canGoNextCardField.value) return;
  if (!canAdvanceCurrentCardField.value) {
    if (currentCardField.value === 'numberExpiry') {
      toast.warning('Preencha número do cartão, mês e ano para avançar.');
    } else if (currentCardField.value === 'name') {
      toast.warning('Preencha o nome do cartão para avançar.');
    } else if (currentCardField.value === 'cvv') {
      toast.warning('Preencha o CVV válido para avançar.');
    }
    return;
  }
  cardFieldIndex.value += 1;
  syncCardPreviewSideByField();
}

function setCardField(fieldName) {
  const idx = cardFieldOrder.indexOf(fieldName);
  if (idx === -1) return;
  cardFieldIndex.value = idx;
  syncCardPreviewSideByField();
}

const cardBin = computed(() => cardForm.number.replace(/\D/g, '').slice(0, 6));

const cardPreviewNumber = computed(() => {
  const digits = cardForm.number.replace(/\D/g, '').slice(0, 16);
  const grouped = digits.replace(/(.{4})/g, '$1 ').trim();
  if (!grouped) return '•••• •••• •••• ••••';
  return grouped;
});

const cardPreviewName = computed(() => {
  const value = cardForm.name?.trim();
  return value ? value.toUpperCase() : 'NOME NO CARTÃO';
});

const cardPreviewExpiry = computed(() => {
  if (!cardForm.expiryMonth || !cardForm.expiryYear) return 'MM/AA';
  return `${cardForm.expiryMonth}/${cardForm.expiryYear.slice(-2)}`;
});

const cardPreviewCvv = computed(() => {
  const value = cardForm.cvv?.replace(/\D/g, '');
  if (!value) return '•••';
  return value.padEnd(3, '•');
});

const detectedCardBrand = computed(() => {
  const bin = cardBin.value;
  if (/^4/.test(bin)) return 'VISA';
  if (/^(5[1-5]|2[2-7])/.test(bin)) return 'MASTERCARD';
  if (/^3[47]/.test(bin)) return 'AMEX';
  if (/^6(?:011|5)/.test(bin)) return 'ELO';
  return 'CRÉDITO';
});

const isCardFormValid = computed(() => {
  if (
    cardForm.number.replace(/\D/g, '').length < 15 ||
    cardForm.name.trim().length < 3 ||
    !cardForm.expiryMonth ||
    !cardForm.expiryYear ||
    cardForm.cvv.length < 3 ||
    !selectedInstallment.value
  ) return false;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const expYear = parseInt(cardForm.expiryYear, 10);
  const expMonth = parseInt(cardForm.expiryMonth, 10);

  if (expYear < currentYear) return false;
  if (expYear === currentYear && expMonth < currentMonth) return false;

  return true;
});

const isStripeFormValid = computed(() => {
  return cardForm.name.trim().length >= 3 && !!selectedInstallment.value && stripeCardComplete.value;
});

watch(() => cardForm.expiryYear, (newYear) => {
  if (!cardForm.expiryMonth) return;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const year = parseInt(newYear, 10);
  const month = parseInt(cardForm.expiryMonth, 10);

  if (year === currentYear && month < currentMonth) {
    cardForm.expiryMonth = '';
    cardGatewayErrors.value.card_expiry_month = '';
    cardGatewayErrors.value.card_expiry_year = '';
  }
});

const totalParaPagar = computed(() => {
  const val = selectedInstallment.value?.total_value ?? totalNumber.value;
  return formatCurrency(val);
});

const payerFirstName = computed(() => {
  const name = (customerStore.activeCustomer?.name || form.name || '').trim();
  if (!name) return '';
  return name.split(/\s+/)[0];
});

const cardStepSubtitle = computed(() => {
  return payerFirstName.value
    ? `${payerFirstName.value}, informe os dados do seu cartão`
    : 'Informe os dados do seu cartão';
});

function handleCardPrimaryAction() {
  if (sendingCard.value) return;

  if (isStripeCardProcessor.value) {
    pagarComCartao();
    return;
  }

  if (currentCardField.value !== 'installment') {
    goNextCardField();
    return;
  }

  pagarComCartao();
}

function montarPayloadCard(stripeCardToken = '') {
  const c = customerStore.activeCustomer || customerStore.customers[0];
  const sanitizedCardNumber = cardForm.number.replace(/\D/g, '').slice(0, 16);
  const fallbackExpiry = `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear() + 3}`;
  const cardExpiry = stripeCardToken
    ? (cardForm.expiryMonth && cardForm.expiryYear ? `${cardForm.expiryMonth}/${cardForm.expiryYear}` : fallbackExpiry)
    : `${cardForm.expiryMonth}/${cardForm.expiryYear}`;
  const customerPostalCode = (c?.postal_code || form.postal_code || '').replace(/\D/g, '');
  const customerAddressNumber = (c?.address_number || form.address_number || '').trim();

  return {
    event_id:          eventId,
    items:             items.value.map(i => ({ id: i.id, price: i.price, quantity: i.quantity })),
    total:             selectedInstallment.value?.total_value ?? totalNumber.value,
    method:            'card',
    customer_name:     c.name,
    customer_cpf_cnpj: c.cpf_cnpj,
    customer_email:    c.email,
    customer_phone:    c.phone,
    customer_postal_code: customerPostalCode,
    customer_address_number: customerAddressNumber,
    customer: {
      name: c.name,
      cpf_cnpj: c.cpf_cnpj,
      email: c.email,
      phone: c.phone,
      postal_code: customerPostalCode,
      address_number: customerAddressNumber,
    },
    card: {
      credit_card_token: stripeCardToken || undefined,
      number:            stripeCardToken ? '' : sanitizedCardNumber,
      name:              cardForm.name.trim(),
      expiry:            cardExpiry,
      cvv:               stripeCardToken ? '' : cardForm.cvv,
      installment:       selectedInstallment.value?.number || 1,
    },
    installment_count: selectedInstallment.value?.number || 1,
    installment_value: selectedInstallment.value?.installment_value || totalNumber.value,
  };
}

async function fetchPaymentProcessorsConfig() {
  try {
    const { data } = await api.get('/config/fees');
    cardProcessor.value = String(data?.card_processor || 'asaas').toLowerCase();
    stripePublishableKey.value = String(data?.stripe_publishable_key || '').trim();
  } catch {
    cardProcessor.value = 'asaas';
    stripePublishableKey.value = '';
  }
}

async function ensureStripeClient() {
  if (!isStripeCardProcessor.value) return null;
  if (stripeClient.value) return stripeClient.value;

  if (!stripePublishableKey.value) {
    throw new Error('Stripe não configurado: chave pública ausente.');
  }

  const client = await loadStripe(stripePublishableKey.value);
  if (!client) {
    throw new Error('Não foi possível inicializar Stripe.js.');
  }

  stripeClient.value = client;
  return client;
}

async function ensureStripeCardElement() {
  if (!isStripeCardProcessor.value || currentStep.value !== 2) return;

  const stripe = await ensureStripeClient();
  if (!stripe) return;

  await nextTick();
  const mountNode = document.getElementById('stripe-card-element');
  if (!mountNode) return;
  if (stripeCardElement.value) return;

  stripeElements.value = stripe.elements();
  const cardElement = stripeElements.value.create('card', {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: '16px',
        color: '#32355d',
        fontFamily: 'Nunito, sans-serif',
        '::placeholder': {
          color: '#9aa0b2',
        },
      },
      invalid: {
        color: '#dc3545',
      },
    },
  });

  cardElement.mount(mountNode);
  cardElement.on('change', (event) => {
    stripeCardComplete.value = !!event.complete;
    stripeCardError.value = event.error?.message || '';
  });

  stripeCardElement.value = cardElement;
}

function destroyStripeCardElement() {
  if (stripeCardElement.value) {
    stripeCardElement.value.destroy();
    stripeCardElement.value = null;
  }
  stripeElements.value = null;
  stripeCardComplete.value = false;
  stripeCardError.value = '';
}

async function createStripeCardToken() {
  const stripe = await ensureStripeClient();
  if (!stripe || !stripeCardElement.value) return '';

  const { token, error } = await stripe.createToken(stripeCardElement.value, {
    name: cardForm.name.trim(),
    address_zip: (customerStore.activeCustomer?.postal_code || form.postal_code || '').replace(/\D/g, ''),
  });
  if (error) {
    throw new Error(error.message || 'Falha ao tokenizar cartão com Stripe.js.');
  }

  return token?.id || '';
}

async function pagarComCartao() {
  if (isStripeCardProcessor.value) {
    if (!isStripeFormValid.value) return;
  } else if (!isCardFormValid.value) {
    return;
  }

  cardGatewayErrors.value = {};
  sendingCard.value    = true;
  loading.value        = true;
  loadingMessage.value = 'Processando pagamento...';

  cardFlow.value       = 'processing';

  try {
    let stripeCardToken = '';
    if (isStripeCardProcessor.value) {
      loadingMessage.value = 'Processando pagamento...';
      stripeCardToken = await createStripeCardToken();
      if (!stripeCardToken) {
        throw new Error('Não foi possível tokenizar o cartão com Stripe.js.');
      }
    }

    const { data } = await api.post(`/payments/card/${eventId}`, montarPayloadCard(stripeCardToken));
    customerStore.setPayment({
      event_id: eventId,
      method:   'card',
      status:   data.status === 'paid' ? 'confirmed' : 'pending',
      data:     { payment_id: data.id },
    });

    if (data.status === 'paid') {
      customerStore.updatePaymentStatus('confirmed');
      mostrarSucesso();
    } else if (data.status === 'waiting_payment' && data.stripe_client_secret && isStripeCardProcessor.value) {
      // Stripe retornou requires_action: 3DS necessário.
      // Abre o popup do banco via Stripe.js — o usuário autentica dentro do iframe da Stripe.
      // Após a autenticação, o webhook payment_intent.succeeded é disparado e o polling confirma.
      loadingMessage.value = 'Aguardando autenticação do banco...';
      const stripeInstance = await ensureStripeClient();
      const { error: nextActionError } = await stripeInstance.handleNextAction({
        clientSecret: data.stripe_client_secret,
      });
      if (nextActionError) {
        cardFlow.value = 'failed';
        showCardDeclinedModal.value = true;
        return;
      }
      // 3DS concluído com sucesso — inicia polling aguardando o webhook confirmar
      iniciarPollingCard(data.id);
    } else if (['processing', 'authorized', 'waiting_payment'].includes(data.status)) {
      // polling para cartão
      iniciarPollingCard(data.id);
    } else {
      // failed, cancelled, expired, refunded
      cardFlow.value = 'failed';
      showCardDeclinedModal.value = true;
    }
  } catch (err) {
    console.error('[PaymentView] Erro ao pagar com cartão:', err);

    const responseStatus = err?.response?.status;
    const responseCode = err?.response?.data?.code;

    if (responseStatus === 422 && responseCode === 'CARD_NOT_AUTHORIZED') {
      cardFlow.value = 'failed';
      showCardDeclinedModal.value = true;
      return;
    }

    const validationErrors = normalizeValidationErrors(err, { defaultStep: 2 });
    if (validationErrors.length > 0) {
      // Reset de UI imediato para não manter spinner/tela de processing ao tratar erro
      cardFlow.value = 'form';
      showCardDeclinedModal.value = false;
      sendingCard.value = false;
      loading.value = false;
      loadingMessage.value = '';

      const firstError = validationErrors[0];
      const targetStep = Number(firstError.step) || 2;

      if (targetStep <= 1) {
        gatewayFieldErrors.value = fieldErrorsToMap(validationErrors.filter(fe => Number(fe.step || 1) <= 1));

        if (shouldOpenGifterEditModalForError(firstError)) {
          await openGifterEditAndFocusField(firstError);
          showUpdateGifterToast(() => {
            openGifterEditAndFocusField(firstError);
          });
        } else {
          currentStep.value = 1;
          showNewForm.value = true;
          await focusElementByIdWithRetry(firstError.field ? `payment-${firstError.field.replace('_', '-')}` : 'payment-postal-code');
          focusPaymentField(firstError.field || 'postal_code');
        }
      } else {
        cardGatewayErrors.value = fieldErrorsToMap(validationErrors);
      }

      toast.error(firstError.message || 'Verifique os dados do cartão e tente novamente.', {
        autoClose: 6000,
        position: 'top-right',
      });
      return;
    }

    toast.error('Não foi possível processar o pagamento. Tente novamente.');
    cardFlow.value = 'failed';
    showCardDeclinedModal.value = true;
  } finally {
    sendingCard.value    = false;
    loading.value        = false;
    loadingMessage.value = '';
  }
}

let cardPollingInterval = null;
let cardPollAttempts    = 0;

function iniciarPollingCard(paymentId) {
  cardPollAttempts = 0;
  cardPollingInterval = setInterval(async () => {
    cardPollAttempts++;
    if (cardPollAttempts >= MAX_POLL_ATTEMPTS) {
      clearInterval(cardPollingInterval);
      customerStore.updatePaymentStatus('failed');
      cardFlow.value = 'failed';
      showCardDeclinedModal.value = true;
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${paymentId}`);
      if (data.status === 'paid') {
        clearInterval(cardPollingInterval);
        customerStore.updatePaymentStatus('confirmed');
        mostrarSucesso();
        return;
      }

      if (['failed', 'cancelled', 'expired', 'refunded'].includes(data.status)) {
        clearInterval(cardPollingInterval);
        customerStore.updatePaymentStatus('failed');
        cardFlow.value = 'failed';
        showCardDeclinedModal.value = true;
        return;
      }

      // 'processing' e 'authorized' — continuar polling silenciosamente
    } catch (e) {
      console.error('[PaymentView] Erro no polling de cartão:', e);
    }
  }, POLLING_INTERVAL * 1000);
}

// ─────────────────────────────────────────────
// Sucesso e Navegação
// ─────────────────────────────────────────────
const loading        = ref(false);
const loadingMessage = ref('');
const showSuccessModal = ref(false);
const eventShareUrl = ref('');

function mostrarSucesso() {
  paymentSucceeded.value = true;
  cart.clearEvent(eventId);
  customerStore.clearPayment();
  showSuccessModal.value = true;
}

function handleSuccessRedirect(redirectEventId) {
  showSuccessModal.value = false;
  router.replace({ name: 'EventDetails', params: { id: redirectEventId } });
}

function handleDeclinedTryAgain() {
  showCardDeclinedModal.value = false;
  cardFlow.value = 'form';
  cardGatewayErrors.value = {};
}

function handleDeclinedUsePix() {
  showCardDeclinedModal.value = false;
  cardFlow.value = 'form';
  cardGatewayErrors.value = {};
  router.replace({
    name: 'PaymentView',
    params: { id: eventId },
    query: { ref: 'pix' },
  });
}

function switchToCard() {
  limparIntervalos();
  customerStore.clearPayment();
  pixFlow.value = 'generating';
  pixErrorDetail.value = null;
  router.replace({
    name: 'PaymentView',
    params: { id: eventId },
    query: { ref: 'card' },
  });
}

function goBackToCheckout() {
  limparIntervalos();
  customerStore.clearPayment();
  pixFlow.value = 'generating';
  pixErrorDetail.value = null;
  router.push({ name: 'Checkout', params: { id: eventId } });
}

function voltar() {
  if (currentStep.value === 2 && method === 'card' && cardFlow.value === 'form' && cardFieldIndex.value > 0) {
    goPrevCardField();
    return;
  }

  if (currentStep.value === 1) {
    if (showNewForm.value && customerAlreadySaved.value) {
      showNewForm.value = false;
      return;
    }

    if (editMode.value) {
      editMode.value = false;
      selectedCpfs.value = [];
      return;
    }

    router.back();
    return;
  }
  // Step 2 PIX ativo → exibir modal de confirmação antes de sair
  if (method === 'pix' && pixFlow.value === 'paying') {
    leaveTarget.value = 'step1';
    showLeaveModal.value = true;
    return;
  }
  // Step 2 → Step 1 (pix não ativo ou cartão)
  if (method === 'pix') {
    limparIntervalos();
    customerStore.clearPayment();
    pixFlow.value = 'generating';
  } else {
    if (cardPollingInterval) clearInterval(cardPollingInterval);
    cardFlow.value = 'form';
    cardGatewayErrors.value = {};
    showCardDeclinedModal.value = false;
  }
  currentStep.value = 1;
  checkoutDisclaimerAccepted.value = false;
}

// ─────────────────────────────────────────────
// onMounted — restaurar do store
// ─────────────────────────────────────────────
onMounted(() => {
  eventShareUrl.value = `${window.location.origin}/event/${eventId}`;

  fetchPaymentProcessorsConfig().then(() => {
    ensureStripeCardElement();
  });

  if (items.value.length === 0) {
    router.replace({ name: 'EventDetails', params: { id: eventId } });
    return;
  }

  // Bloquear checkout se evento estiver em rascunho
  const cartEvent = cart.getEvent(eventId);
  if (cartEvent?.status === 'draft') {
    router.replace(`/checkout/${eventId}`);
    return;
  }

  // Restaurar dados do cliente se existirem
  const c = customerStore.activeCustomer;
  if (c?.cpf_cnpj) {
    form.name     = c.name;
    form.email    = c.email;
    form.phone    = c.phone;
    form.postal_code = c.postal_code || '';
    form.address_number = c.address_number || '';
    // restaurar CPF mascarado
    const digits = c.cpf_cnpj;
    if (digits.length === 11) {
      form.cpf_cnpj = `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9)}`;
    } else if (digits.length === 14) {
      form.cpf_cnpj = `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
    }
  }

  // Restaurar pagamento PIX pendente
  if (method === 'pix' && customerStore.hasPendingPayment(eventId)) {
    const pd = customerStore.payment.data;
    pixData.qrcode_image = pd.qrcode_image;
    pixData.pix_key      = pd.pix_key;
    pixData.payment_id   = pd.payment_id;
    pixFlow.value        = 'paying';
    currentStep.value    = 2;
    iniciarPolling();
  }

  window.addEventListener('beforeunload', handleBeforeUnload);
});

watch([isStripeCardProcessor, currentStep], async ([isStripe, step]) => {
  if (isStripe && step === 2) {
    await ensureStripeCardElement();
    return;
  }

  if (!isStripe || step !== 2) {
    destroyStripeCardElement();
  }
});

onBeforeUnmount(() => {
  limparIntervalos();
  if (cardPollingInterval) clearInterval(cardPollingInterval);
  if (cepLookupTimer) clearTimeout(cepLookupTimer);
  if (cepCompleteLookupTimer) clearTimeout(cepCompleteLookupTimer);
  if (cepAbortCtrl) cepAbortCtrl.abort();
  if (cepCompleteAbortCtrl) cepCompleteAbortCtrl.abort();
  destroyStripeCardElement();
  window.removeEventListener('beforeunload', handleBeforeUnload);
  _mq.removeEventListener('change', _mqHandler);
});

// Guard nativo de browser (F5 / fechar aba)
function handleBeforeUnload(e) {
  if (!paymentSucceeded.value && currentStep.value === 2) {
    e.preventDefault();
    e.returnValue = '';
  }
}

// Guard de rota: mostra BottomModal se pagamento pendente
onBeforeRouteLeave(async () => {
  if (paymentSucceeded.value || currentStep.value === 1) return true;
  return new Promise(resolve => {
    leaveTarget.value   = 'route';
    showLeaveModal.value = true;
    pendingLeaveResolve  = resolve;
  });
});

function confirmarSaida() {
  limparIntervalos();
  if (cardPollingInterval) clearInterval(cardPollingInterval);
  customerStore.clearPayment();
  showLeaveModal.value = false;
  if (leaveTarget.value === 'step1') {
    // Voltou via botão Voltar → retornar ao step 1
    pixFlow.value = 'generating';
    currentStep.value = 1;
    if (pendingLeaveResolve) { pendingLeaveResolve(false); pendingLeaveResolve = null; }
  } else {
    // Saída de rota → liberar a navegação
    if (pendingLeaveResolve) { pendingLeaveResolve(true); pendingLeaveResolve = null; }
  }
}

function cancelarSaida() {
  showLeaveModal.value = false;
  leaveTarget.value    = 'route';
  if (pendingLeaveResolve) { pendingLeaveResolve(false); pendingLeaveResolve = null; }
}
</script>

<style scoped>
.disclaimer-box {
  background: #fffaf5;
  border: 1px solid #f8d8e8;
  border-radius: 0.75rem;
  padding: 0.75rem 0.8rem;
  font-size: 0.89rem;
}

.disclaimer-box--error {
  border: 1.5px dashed #e74c3c;
  background: #fff5f5;
}

.disclaimer-box .form-check-label {
  color: #5f6373 !important;
  line-height: 1.45;
}

.create-event-page {
  display: flex;
  flex-direction: column;
  background-color: var(--event-bg, #f8f9fa);
  height: 100%;
  width: 100%;
  min-width: 600px;
  overflow-x: hidden;
}

/* ── Steps Nav ── */
.steps-navigation {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 90;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem 1.5rem 0.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  margin-top: 0;
}

.progress-track {
  position: relative;
  height: 3px;
  background-color: #e9ecef;
  border-radius: 99px;
  margin: 0 auto 1rem;
  max-width: 320px;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--event-primary, #ff50a3), color-mix(in srgb, var(--event-primary, #ff50a3) 65%, #fff));
  border-radius: 99px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.steps-row {
  display: flex;
  justify-content: space-around;
  max-width: 360px;
  margin: 0 auto;
  gap: 8px;
}

.step-dot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  gap: 5px;
  opacity: 0.45;
  transition: opacity 0.2s;
  min-width: 0;
  flex: 1;
}

.step-dot-item.active,
.step-dot-item.completed {
  opacity: 1;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9e9e9e;
  transition: all 0.3s ease;
}

.step-dot-item.active .step-dot {
  background-color: var(--event-primary, #ff50a3);
  color: #fff;
  box-shadow: 0 0 0 4px var(--event-primary-shadow, rgba(255, 80, 163, 0.18));
}

.step-dot-item.completed .step-dot {
  background-color: #32355d;
  color: #fff;
}

.step-dot-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #9e9e9e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.step-dot-item.active .step-dot-label {
  color: var(--event-primary, #ff50a3);
}

.step-dot-item.completed .step-dot-label {
  color: #32355d;
}

/* ── Conteúdo ── */
.step-content-wrapper {
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  padding: 2.5rem;
}

.step-inner {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow: hidden;
}

.step-header { text-align: left; }

.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.25rem;
}

.step-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0;
}

.payment-form-label {
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.5rem;
  display: block;
}

.payment-form-control {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  font-size: 1rem;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.payment-form-control:focus {
  border-color: var(--event-primary, #ff50a3);
  box-shadow: 0 0 0 0.25rem var(--event-primary-shadow, rgba(255, 80, 163, 0.25));
  outline: none;
}

/* ── Step 1 (Identificação) — padrão Register ── */
.identity-form-card .payment-form-label,
.identity-form-card :deep(.form-label) {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.identity-form-card .payment-form-control,
.identity-form-card :deep(.form-control) {
  background-color: var(--input-bg-soft, #fcfcfd);
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.identity-form-card .payment-form-control::placeholder,
.identity-form-card :deep(.form-control::placeholder) {
  color: #c0c0c0;
  font-size: 0.88rem;
}

.identity-form-card .payment-form-control:focus,
.identity-form-card :deep(.form-control:focus) {
  background-color: #fff;
  border-color: var(--event-primary, #ff50a3);
  box-shadow: 0 0 0 3px var(--event-primary-shadow, rgba(255, 80, 163, 0.12));
  outline: none;
}

.identity-form-card .payment-form-control.is-invalid,
.identity-form-card :deep(.form-control.is-invalid) {
  border-color: #e0185d;
  background-image: none;
}

.identity-form-card :deep(.text-danger),
.identity-form-card :deep(.invalid-feedback) {
  font-size: 0.78rem;
  color: #e0185d;
  margin-top: 0.25rem;
}

/* ── PIX ── */
.qr-code {
  width: 200px;
  height: 200px;
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1);
  border-radius: 0.75rem;
  display: block;
  margin: 0 auto;
}

.pix-key-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  overflow: hidden;
  min-width: 0;
}

.pix-key-text {
  word-break: break-all;
  overflow-wrap: break-word;
  min-width: 0;
  flex: 1;
}

.text-pink { color: var(--event-primary, #ff50a3); }

@keyframes pulse-fade {
  0%   { opacity: 0.7; }
  50%  { opacity: 1; }
  100% { opacity: 0.7; }
}
.pulse-text { animation: pulse-fade 1.5s infinite ease-in-out; }

/* ── Footer ── */
.app-footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  box-shadow: 0 -2px 4px rgba(0,0,0,0.08);
  padding: 0.75rem 1rem;
  z-index: 100;
  flex-shrink: 0;
  background-color: #fff;
}

.footer-inner {
  max-width: 700px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.btn-nav {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  flex-grow: 1;
  margin: 0 0.5rem;
  max-width: 50%;
}

.btn-nav:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-color-site {
  background-color: var(--event-primary, #ff50a3);
  color: #fff;
  border: 1px solid var(--event-primary, #ff50a3);
  transition: background-color 0.2s ease;
}
.btn-color-site:hover {
  background-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  color: #fff;
}
.btn-color-site:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-pay-highlight {
  background-color: var(--event-primary, #ff50a3);
  color: #fff;
  border: 1px solid var(--event-primary, #ff50a3);
  font-weight: 700;
  box-shadow: 0 4px 12px var(--event-primary-shadow, rgba(255, 80, 163, 0.22));
  max-width: none;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-pay-highlight__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.btn-pay-highlight:hover {
  background-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  color: #fff;
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
}

.btn-pay-highlight:disabled {
  background-color: #ea8fbd;
  border-color: #ea8fbd;
  color: #fff;
  opacity: 1;
  box-shadow: none;
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}
.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: #fff;
}
.btn-outline-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border.text-pink { color: var(--event-primary, #ff50a3) !important; }

@media (max-width: 767.98px) {
  .step-inner {
    padding-top: 3rem;
  }

  .create-event-page {
    transform: translateZ(0);
    min-width: 0;
    margin-top: -1rem !important;
    height: calc(100% + 1rem);
  }

  .identity-card {
    gap: 0.55rem;
    padding: 0.75rem 0.85rem;
    overflow: hidden;
  }

  .identity-avatar {
    display: none;
  }

  .identity-info {
    min-width: 0;
    flex: 1 1 auto;
  }

  .identity-name,
  .identity-email {
    display: block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .steps-navigation {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 200;
    margin-top: -1rem;
    padding: 0.75rem 1rem 0.65rem;
  }

  .step-dot {
    width: 26px;
    height: 26px;
  }

  .step-dot-label {
    font-size: 0.68rem;
  }

  .btn-nav {
    font-size: 0.9rem;
    padding: 0.6rem 1rem;
    min-width: 0;
  }

  .footer-inner {
    gap: 0.5rem;
  }

  .btn-pay-highlight {
    padding-left: 0.7rem;
    padding-right: 0.7rem;
  }

  .btn-pay-highlight__label {
    white-space: normal;
    text-align: center;
    line-height: 1.15;
    font-size: 0.9rem;
  }

  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1031;
  }
  .step-content-wrapper {
    padding-bottom: 80px;
  }
}

/* ── Identity card (boas-vindas) ── */
.identity-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  user-select: none;
}
.identity-card + .identity-card {
  margin-top: 0.5rem;
}
.identity-card:hover {
  border-color: var(--event-primary, #ff50a3);
}
.identity-card.is-selected {
  border-color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f7);
}
.identity-card.is-edit-mode {
  cursor: pointer;
}
.identity-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  flex-shrink: 0;
}
.identity-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.identity-name {
  font-weight: 700;
  color: #32355d;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.identity-email {
  font-size: 0.8rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.identity-confirm-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;
}
.identity-confirm-btn:hover {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
}

.customer-incomplete-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 600;
  color: #f59e0b;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.complete-profile-modal {
  padding: 0.25rem 0 0.5rem;
}

.complete-modal-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1.5rem;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, color-mix(in srgb, var(--event-primary, #ff50a3) 8%, #fff), color-mix(in srgb, var(--event-primary, #ff50a3) 15%, #fff));
  border-radius: 0.875rem;
  border: 1px solid color-mix(in srgb, var(--event-primary, #ff50a3) 25%, transparent);
}

.complete-modal-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--event-primary, #ff50a3), color-mix(in srgb, var(--event-primary, #ff50a3) 65%, #fff));
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.complete-modal-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.2rem;
}

.complete-modal-sub {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.45;
}

.complete-modal-fields {
  padding: 0 0.25rem;
}
.link-switch {
  font-size: 0.85rem;
  color: #6c757d;
  text-decoration: none;
}
.link-switch:hover {
  color: var(--event-primary, #ff50a3);
  text-decoration: underline;
}

/* ── Edit mode da lista ── */
.btn-edit-mode {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1.5px solid #dee2e6;
  background: #fff;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}
.btn-edit-mode:hover,
.btn-edit-mode.is-active {
  border-color: var(--event-primary, #ff50a3);
  color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f7);
}

.identity-checkbox {
  font-size: 1.1rem;
  color: #adb5bd;
  flex-shrink: 0;
  transition: color 0.15s;
}
.identity-card.is-selected .identity-checkbox {
  color: var(--event-primary, #ff50a3);
}

.edit-action-bar {
  margin-top: 0.75rem;
}

/* Animação barra de edição */
.fade-bar-enter-active,
.fade-bar-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.fade-bar-enter-from,
.fade-bar-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── PIX paying redesign ── */
.pix-paying-wrapper {
  padding: 0.5rem 0;
}
.pix-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
}
.pix-secure-badge {
  font-size: 0.75rem;
  color: #28a745;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.pix-amount-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.pix-amount-label {
  font-size: 0.7rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.pix-amount-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #32355d;
}
.qr-code-wrapper {
  text-align: center;
  margin-bottom: 1rem;
}
.qr-hint {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.5rem;
  margin-bottom: 0;
}
.pix-or-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;
  color: #adb5bd;
  font-size: 0.8rem;
}
.pix-or-divider::before,
.pix-or-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #dee2e6;
}
.pix-copy-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
  border: 1.5px dashed #dee2e6;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  overflow: hidden;
  min-width: 0;
  gap: 0.75rem;
}
.pix-copy-btn:hover {
  border-color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f7);
}
.pix-copy-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--event-primary, #ff50a3);
  white-space: nowrap;
}

/* ── Countdown ── */
.pix-countdown {
  margin-top: 1.25rem;
}
.pix-countdown-bar {
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}
.pix-countdown-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--event-primary, #ff50a3), #32355d);
  border-radius: 2px;
  transition: width 1s linear;
}
.pix-countdown-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}
.pix-countdown-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  animation: pulse-dot 1.5s infinite ease-in-out;
  flex-shrink: 0;
}
.pix-countdown-seconds {
  color: #32355d;
}

/* ── Card preview (step cartão) ── */
.card-preview-section {
  width: 100%;
}

.card-preview {
  position: relative;
  width: 100%;
  max-width: 330px;
  margin: 0 auto;
  min-height: 186px;
  perspective: 1200px;
}

.card-preview__face {
  position: absolute;
  inset: 0;
  border-radius: 18px;
  padding: 0.88rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #fff;
  background: linear-gradient(135deg, #e33370 0%, #a62455 100%);
  box-shadow: 0 12px 28px rgba(227, 51, 112, 0.22);
  backface-visibility: hidden;
  transform-style: preserve-3d;
  transition: transform 0.55s ease;
  overflow: hidden;
}

.card-preview__face::before {
  content: '';
  position: absolute;
  width: 220px;
  height: 220px;
  top: -120px;
  right: -120px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
}

.card-preview__face::after {
  content: '';
  position: absolute;
  width: 170px;
  height: 170px;
  bottom: -70px;
  left: -70px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.card-preview__face--front {
  transform: rotateY(0deg);
}

.card-preview__face--back {
  transform: rotateY(180deg);
}

.card-preview.is-back .card-preview__face--front {
  transform: rotateY(-180deg);
}

.card-preview.is-back .card-preview__face--back {
  transform: rotateY(0deg);
}

.card-preview__top,
.card-preview__bottom,
.card-preview__number,
.card-preview__cvv-wrap,
.card-preview__back-footer {
  position: relative;
  z-index: 1;
}

.card-preview__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-preview__chip {
  width: 46px;
  height: 34px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f4d06f, #cf9726);
}

.card-preview__brand {
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.card-preview__number {
  margin: 0.55rem 0 0.45rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  word-spacing: 0.14em;
}

.card-preview__bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
}

.card-preview__label {
  display: block;
  font-size: 0.64rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.8;
}

.card-preview__label--dark {
  color: #6c757d;
  opacity: 1;
}

.card-preview__value {
  margin: 0.2rem 0 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-preview__stripe {
  position: relative;
  z-index: 1;
  width: calc(100% + 1.9rem);
  margin: 0 -0.95rem;
  margin-top: 0.45rem;
  height: 30px;
  background: #1f2937;
}

.card-preview__cvv-wrap {
  margin-top: 0.75rem;
}

.card-preview__cvv-box {
  margin-top: 0.3rem;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  text-align: right;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 0.3rem 0.55rem;
}

.card-preview__back-footer {
  margin-top: auto;
  font-size: 0.72rem;
  font-weight: 600;
  opacity: 0.88;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-step-field-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-step-field-main {
  flex: 1;
  min-width: 0;
}

.card-number-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.card-number-inline__number {
  flex: 1;
  min-width: 0;
}

.card-number-inline__month,
.card-number-inline__year {
  width: 96px;
  flex: 0 0 96px;
}

.card-step-arrow {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  background-color: var(--event-primary, #ff50a3);
  border-color: var(--event-primary, #ff50a3);
  color: #fff;
}

.card-step-arrow:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  color: #fff;
}

.card-step-arrow:disabled {
  background-color: #f5f6f8;
  border-color: #dee2e6;
  color: #adb5bd;
}

.card-step-arrow-spacer {
  width: 36px;
  flex: 0 0 36px;
}

.checkout-card-step .form-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.checkout-card-step .payment-form-control,
.checkout-card-step .form-select {
  background-color: var(--input-bg-soft, #fcfcfd);
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.checkout-card-step .payment-form-control::placeholder,
.checkout-card-step .form-select::placeholder {
  color: #c0c0c0;
  font-size: 0.88rem;
}

.checkout-card-step .payment-form-control:focus,
.checkout-card-step .form-select:focus {
  background-color: #fff;
  border-color: var(--event-primary, #ff50a3);
  box-shadow: 0 0 0 3px var(--event-primary-shadow, rgba(255, 80, 163, 0.12));
  outline: none;
}

.checkout-card-step .payment-form-control.is-invalid,
.checkout-card-step .form-select.is-invalid {
  border-color: #e0185d;
  background-image: none;
}

.checkout-card-step .invalid-feedback {
  font-size: 0.78rem;
  color: #e0185d;
  margin-top: 0.25rem;
}

.stripe-card-element-wrap {
  background-color: var(--input-bg-soft, #fcfcfd);
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.78rem 1rem;
}

.stripe-card-element {
  min-height: 20px;
}

.stripe-card-element-wrap:focus-within {
  background-color: #fff;
  border-color: var(--event-primary, #ff50a3);
  box-shadow: 0 0 0 3px var(--event-primary-shadow, rgba(255, 80, 163, 0.12));
}

.installment-field :deep(.form-label) {
  display: none;
}

.installment-field > .form-label {
  display: block;
}

.installment-field :deep(.mb-3) {
  margin-bottom: 0 !important;
}

.installment-field :deep(.form-select) {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.installment-field :deep(.form-select:focus) {
  background-color: #fff;
  border-color: var(--event-primary, #ff50a3);
  box-shadow: 0 0 0 3px var(--event-primary-shadow, rgba(255, 80, 163, 0.12));
  outline: none;
}

@media (max-width: 575.98px) {
  .card-preview {
    max-width: 315px;
    min-height: 178px;
  }

  .card-preview__face {
    padding: 0.82rem;
  }

  .card-preview__number {
    font-size: 0.96rem;
    margin-top: 0.5rem;
  }

  .card-preview__value {
    max-width: 120px;
  }

  .card-number-inline__month,
  .card-number-inline__year {
    width: 78px;
    flex: 0 0 78px;
  }
}

@media (max-width: 767.98px) {
  .identity-card {
    gap: 0.55rem;
    padding: 0.75rem 0.85rem;
    overflow: hidden;
  }

  .identity-avatar {
    display: none !important;
  }

  .identity-info {
    min-width: 0;
    flex: 1 1 auto;
  }

  .identity-name,
  .identity-email {
    display: block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@keyframes pulse-dot {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50%       { transform: scale(1.4); opacity: 1; }
}
</style>
